import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { adToBs, bsToAd, bsMonthName, bsYearsRange } from "../lib/nepaliDate";
import { transliterate } from "../lib/transliterate";
import {
    CalendarDays,
    Coins,
    ArrowLeftRight,
    RefreshCw,
    TrendingUp,
    TrendingDown,
    PartyPopper,
    Fuel,
    CloudSun,
    Cloud,
    Sun,
    CloudRain,
    CloudSnow,
    CloudFog,
    CloudLightning,
    Wind,
    Droplets,
    Flame,
    Sparkles,
    Languages,
    Copy,
    Check,
    FileText,
    Eraser,
} from "lucide-react";

export default function Utilities() {
    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-6 sm:space-y-8">
            <div>
                <p className="eyebrow">Nepal Utilities</p>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mt-2 leading-tight">
                    Handy tools for daily life{" "}
                    <span className="font-nepali text-[var(--lt-terracotta)]">
                        नेपाल
                    </span>
                </h1>
                <p className="text-[var(--lt-muted)] mt-3 max-w-2xl text-sm sm:text-base">
                    Convert dates, track today's precious-metal rates and foreign
                    exchange — all from Nepali sources.
                </p>
            </div>

            <nav className="flex flex-wrap gap-x-4 gap-y-2 text-sm" data-testid="utilities-tool-links">
                {Object.entries(UTILITY_TOOLS).map(([slug, tool]) => (
                    <Link key={slug} to={`/${slug}`} className="text-[var(--lt-terracotta)] hover:underline" data-testid={`utility-link-${slug}`}>
                        {tool.name}
                    </Link>
                ))}
            </nav>

            <div className="grid gap-5 sm:gap-6 lg:grid-cols-2">
                <DateConverter />
                <TransliterationCard />
                <BullionCard />
                <WordCounterCard />
                <ForexCard />
                <HolidaysCard />
                <FuelCard />
                <WeatherCard />
            </div>
        </div>
    );
}

export const UTILITY_TOOLS = {
    "date-converter": { name: "Date Converter", C: DateConverter, intro: "Convert any date between the Bikram Sambat (BS) calendar used in Nepal and the Gregorian (AD) calendar. The conversion runs in your browser using a built-in table of Nepali month lengths for BS years 2000 to 2099." },
    transliteration: { name: "Transliteration", C: TransliterationCard, intro: "Type Nepali words using English letters and see them in Devanagari as you type, then copy the result. It works in your browser with simple phonetic rules, so your text is not sent anywhere. Check unusual words before using them." },
    "gold-silver-rates": { name: "Gold & Silver Rates", C: BullionCard, intro: "See today's prices for hallmark gold, tejabi gold and silver per tola in Nepali rupees. Rates are fetched from the Federation of Nepal Gold and Silver Dealers' Association (FeNeGoSiDA)." },
    "word-counter": { name: "Word Counter", C: WordCounterCard, intro: "Paste or type text in English or Nepali to count its words, characters and sentences. Counting happens in your browser, and your text is not saved or sent anywhere." },
    "forex-rates": { name: "Forex Rates", C: ForexCard, intro: "Check buying and selling rates for foreign currencies against the Nepali rupee (NPR). Rates are fetched from Nepal Rastra Bank's published exchange rates." },
    holidays: { name: "Holidays & Festivals", C: HolidaysCard, intro: "See upcoming national festivals and public holidays in Nepal. The list is kept by LipiTyping based on the Government of Nepal's official calendar, and dates can change, so confirm important dates with official notices." },
    "fuel-prices": { name: "Fuel Prices", C: FuelCard, intro: "See retail prices for petrol, diesel and kerosene in Nepal. Prices are kept by LipiTyping based on rates published by Nepal Oil Corporation (NOC), and may lag behind a new price change." },
    weather: { name: "Weather", C: WeatherCard, intro: "See current temperature, conditions, humidity and wind for Kathmandu, Pokhara, Biratnagar and Lalitpur. Weather data comes from the Open-Meteo service." },
};

export function UtilityToolPage({ slug }) {
    const Tool = UTILITY_TOOLS[slug].C;
    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-6" data-testid={`utility-page-${slug}`}>
            <Link to="/utilities" className="text-sm text-[var(--lt-muted)] hover:text-[var(--lt-text)]" data-testid="utility-back-link">
                ◀ Return to Utilities Hub
            </Link>
            <p className="text-[var(--lt-muted)] text-sm sm:text-base leading-relaxed" data-testid="utility-intro">
                {UTILITY_TOOLS[slug].intro}
            </p>
            <Tool />
            <nav className="pt-2" data-testid="utility-other-tools">
                <p className="eyebrow mb-2">Other tools</p>
                <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
                    {Object.entries(UTILITY_TOOLS)
                        .filter(([s]) => s !== slug)
                        .map(([s, t]) => (
                            <Link key={s} to={`/${s}`} className="text-[var(--lt-terracotta)] hover:underline" data-testid={`other-tool-link-${s}`}>
                                {t.name}
                            </Link>
                        ))}
                </div>
            </nav>
        </div>
    );
}

/* ---------- Date Converter ---------- */
function DateConverter() {
    const today = new Date();
    const range = bsYearsRange();
    const [direction, setDirection] = useState("ad_to_bs");
    const [ad, setAd] = useState({
        y: today.getFullYear(),
        m: today.getMonth() + 1,
        d: today.getDate(),
    });
    const [bs, setBs] = useState(() => {
        try {
            return adToBs(today.getFullYear(), today.getMonth() + 1, today.getDate());
        } catch {
            return { year: 2081, month: 1, day: 1 };
        }
    });
    const [error, setError] = useState("");

    const convert = () => {
        setError("");
        try {
            if (direction === "ad_to_bs") {
                const r = adToBs(Number(ad.y), Number(ad.m), Number(ad.d));
                setBs({ year: r.year, month: r.month, day: r.day });
            } else {
                const r = bsToAd(Number(bs.year), Number(bs.month), Number(bs.day));
                setAd({ y: r.year, m: r.month, d: r.day });
            }
        } catch (e) {
            setError(e.message);
        }
    };

    useEffect(() => {
        convert();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [direction, ad.y, ad.m, ad.d, bs.year, bs.month, bs.day]);

    return (
        <div className="glass rounded-2xl p-5 sm:p-8" data-testid="util-date-converter">
            <Header
                icon={<CalendarDays className="text-[var(--lt-cyan)]" />}
                label="Date Converter"
                title="Bikram Sambat ↔ AD"
                description="Convert calendar dates instantly between Bikram Sambat (BS) and Gregorian (AD) formats."
            />

            <div className="mt-6 flex items-center gap-3">
                <button
                    onClick={() =>
                        setDirection((d) => (d === "ad_to_bs" ? "bs_to_ad" : "ad_to_bs"))
                    }
                    className="btn-ghost-line text-sm inline-flex items-center gap-2"
                    data-testid="date-swap-btn"
                >
                    <ArrowLeftRight size={14} /> Swap direction
                </button>
                <span className="text-xs text-[var(--lt-muted)] font-mono-stat">
                    {direction === "ad_to_bs" ? "AD → BS" : "BS → AD"}
                </span>
            </div>

            <div className="mt-6 grid sm:grid-cols-2 gap-6">
                {/* AD side */}
                <div
                    className={`rounded-xl p-4 sm:p-5 border transition-all ${
                        direction === "ad_to_bs"
                            ? "border-[var(--lt-terracotta)] bg-[var(--lt-bg-2)]/60"
                            : "border-[var(--lt-border)]"
                    }`}
                >
                    <div className="eyebrow mb-3">Gregorian (AD)</div>
                    <div className="grid grid-cols-[1.4fr_1fr_1fr] gap-2">
                        <NumInput
                            label="YYYY"
                            value={ad.y}
                            min={1944}
                            max={2143}
                            disabled={direction !== "ad_to_bs"}
                            onChange={(v) => setAd((s) => ({ ...s, y: v }))}
                            testid="ad-year-input"
                        />
                        <NumInput
                            label="MM"
                            value={ad.m}
                            min={1}
                            max={12}
                            disabled={direction !== "ad_to_bs"}
                            onChange={(v) => setAd((s) => ({ ...s, m: v }))}
                            testid="ad-month-input"
                        />
                        <NumInput
                            label="DD"
                            value={ad.d}
                            min={1}
                            max={31}
                            disabled={direction !== "ad_to_bs"}
                            onChange={(v) => setAd((s) => ({ ...s, d: v }))}
                            testid="ad-day-input"
                        />
                    </div>
                    <div className="mt-4 font-mono-stat text-xl sm:text-2xl break-all" data-testid="ad-result">
                        {String(ad.y).padStart(4, "0")}-{String(ad.m).padStart(2, "0")}-
                        {String(ad.d).padStart(2, "0")}
                    </div>
                </div>

                {/* BS side */}
                <div
                    className={`rounded-xl p-4 sm:p-5 border transition-all ${
                        direction === "bs_to_ad"
                            ? "border-[var(--lt-terracotta)] bg-[var(--lt-bg-2)]/60"
                            : "border-[var(--lt-border)]"
                    }`}
                >
                    <div className="eyebrow mb-3">Bikram Sambat (BS)</div>
                    <div className="grid grid-cols-[1.4fr_1fr_1fr] gap-2">
                        <NumInput
                            label="YYYY"
                            value={bs.year}
                            min={range.min}
                            max={range.max}
                            disabled={direction !== "bs_to_ad"}
                            onChange={(v) => setBs((s) => ({ ...s, year: v }))}
                            testid="bs-year-input"
                        />
                        <NumInput
                            label="MM"
                            value={bs.month}
                            min={1}
                            max={12}
                            disabled={direction !== "bs_to_ad"}
                            onChange={(v) => setBs((s) => ({ ...s, month: v }))}
                            testid="bs-month-input"
                        />
                        <NumInput
                            label="DD"
                            value={bs.day}
                            min={1}
                            max={32}
                            disabled={direction !== "bs_to_ad"}
                            onChange={(v) => setBs((s) => ({ ...s, day: v }))}
                            testid="bs-day-input"
                        />
                    </div>
                    <div className="mt-4 font-mono-stat text-xl sm:text-2xl break-all" data-testid="bs-result">
                        {bs.year}-{String(bs.month).padStart(2, "0")}-
                        {String(bs.day).padStart(2, "0")}
                    </div>
                    <div className="mt-1 text-sm text-[var(--lt-muted)] font-nepali">
                        {bsMonthName(bs.month, "ne")} {bs.day}, {bs.year}
                    </div>
                </div>
            </div>

            {error && (
                <p className="mt-4 text-sm text-red-400" data-testid="date-error">
                    {error}
                </p>
            )}
            <p className="mt-5 text-xs text-[var(--lt-muted)]">
                Supported range: BS 2000 – 2099 (AD 1943 – 2043).
            </p>
        </div>
    );
}

function NumInput({ label, value, min, max, disabled, onChange, testid }) {
    return (
        <label className="block min-w-0">
            <span className="eyebrow text-[10px]">{label}</span>
            <input
                type="number"
                min={min}
                max={max}
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(Number(e.target.value))}
                className="mt-1 w-full min-w-0 bg-[var(--lt-bg-2)] border border-[var(--lt-border)] rounded-lg px-2 sm:px-3 py-2 font-mono-stat text-base sm:text-lg focus:outline-none focus:border-[var(--lt-terracotta)] disabled:opacity-60"
                data-testid={testid}
            />
        </label>
    );
}

/* ---------- Bullion ---------- */
function BullionCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const load = async () => {
        setLoading(true);
        setErr("");
        try {
            const r = await api.get("/utils/bullion");
            setData(r.data);
        } catch (e) {
            setErr("Unable to load rates right now.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    return (
        <div className="glass rounded-2xl p-5 sm:p-8" data-testid="util-bullion">
            <div className="flex items-start justify-between">
                <Header
                    icon={<Coins className="text-[var(--lt-amber)]" />}
                    label="Bullion Rates"
                    title="Gold & Silver (per Tola)"
                    description="View today's official hallmark gold, tejabi gold, and silver market pricing per Tola."
                />
                <button
                    onClick={load}
                    className="btn-ghost-line text-xs inline-flex items-center gap-1"
                    data-testid="bullion-refresh-btn"
                >
                    <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {loading && <SkeletonRows count={3} />}
            {err && !loading && <p className="mt-6 text-sm text-red-400">{err}</p>}

            {data && !loading && (
                <>
                    <ul className="mt-6 divide-y divide-[var(--lt-border)]">
                        {data.items?.map((it) => (
                            <li
                                key={it.key}
                                className="py-4 flex items-center justify-between gap-3"
                                data-testid={`bullion-${it.key}`}
                            >
                                <div className="min-w-0">
                                    <div className="font-semibold text-sm sm:text-base truncate">
                                        {it.en}
                                    </div>
                                    <div className="font-nepali text-xs sm:text-sm text-[var(--lt-muted)] truncate">
                                        {it.np} · {it.unit}
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="font-mono-stat text-lg sm:text-xl">
                                        Rs {formatNPR(it.today)}
                                    </div>
                                    <div
                                        className={`text-xs inline-flex items-center gap-1 mt-0.5 font-mono-stat ${
                                            it.change >= 0
                                                ? "text-[var(--lt-emerald)]"
                                                : "text-red-400"
                                        }`}
                                    >
                                        {it.change >= 0 ? (
                                            <TrendingUp size={12} />
                                        ) : (
                                            <TrendingDown size={12} />
                                        )}
                                        {it.change >= 0 ? "+" : ""}
                                        {formatNPR(it.change)} ({it.change_pct}%)
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                    <FootMeta date={data.date} source={data.source} />
                </>
            )}
        </div>
    );
}

/* ---------- Forex ---------- */
function ForexCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const load = async () => {
        setLoading(true);
        setErr("");
        try {
            const r = await api.get("/utils/forex");
            setData(r.data);
        } catch (e) {
            setErr("Unable to load forex rates right now.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const flag = { USD: "🇺🇸", EUR: "🇪🇺", GBP: "🇬🇧", AUD: "🇦🇺", AED: "🇦🇪" };

    return (
        <div
            className="glass rounded-2xl p-5 sm:p-8 lg:col-span-2"
            data-testid="util-forex"
        >
            <div className="flex items-start justify-between">
                <Header
                    icon={<ArrowLeftRight className="text-[var(--lt-emerald)]" />}
                    label="Forex Rates"
                    title="Foreign Currencies vs NPR"
                    description="Check real-time foreign currency exchange buying and selling values against the Nepali Rupee (NPR)."
                />
                <button
                    onClick={load}
                    className="btn-ghost-line text-xs inline-flex items-center gap-1"
                    data-testid="forex-refresh-btn"
                >
                    <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {loading && <SkeletonRows count={5} />}
            {err && !loading && <p className="mt-6 text-sm text-red-400">{err}</p>}

            {data && !loading && (
                <>
                    <div className="mt-6 grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
                        {data.rates?.map((r) => (
                            <div
                                key={r.iso}
                                className="rounded-xl border border-[var(--lt-border)] bg-[var(--lt-bg-2)]/60 p-4"
                                data-testid={`forex-${r.iso}`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-2xl">{flag[r.iso] || "🌐"}</span>
                                    <span className="eyebrow">{r.iso}</span>
                                </div>
                                <div className="mt-3 text-xs text-[var(--lt-muted)]">
                                    {r.unit > 1 ? `${r.unit} ` : "1 "}
                                    {r.iso} =
                                </div>
                                <div className="font-mono-stat text-2xl mt-0.5">
                                    Rs {r.sell.toFixed(2)}
                                </div>
                                <div className="mt-2 grid grid-cols-2 gap-1 text-[11px] font-mono-stat">
                                    <span className="text-[var(--lt-muted)]">
                                        Buy: {r.buy.toFixed(2)}
                                    </span>
                                    <span className="text-[var(--lt-muted)] text-right">
                                        Sell: {r.sell.toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <FootMeta date={data.date} source={data.source} />
                </>
            )}
        </div>
    );
}

/* ---------- English → Nepali Transliteration ---------- */
function TransliterationCard() {
    const [input, setInput] = useState("namaste, nepaal ko rajdhani kathmandu ho.");
    const [copied, setCopied] = useState(false);
    const output = transliterate(input);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(output);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch (_) {
            /* clipboard denied */
        }
    };

    return (
        <div
            className="glass rounded-2xl p-5 sm:p-8"
            data-testid="util-transliteration"
        >
            <Header
                icon={<Languages className="text-[var(--lt-emerald)]" />}
                label="Transliteration Tool"
                title="English → Nepali"
                description={
                    "Type using Romanized English phonetics (e.g., 'nepal') to output pure Nepali Devanagari text ('नेपाल')."
                }
            />

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div>
                    <label className="eyebrow block mb-2">
                        Romanized input
                    </label>
                    <textarea
                        rows={6}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        className="w-full bg-[var(--lt-bg-2)] border border-[var(--lt-border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--lt-terracotta)] resize-none text-base leading-relaxed"
                        placeholder="e.g., namaste, mero naam ram ho"
                        data-testid="transliterate-input"
                    />
                </div>
                <div>
                    <label className="eyebrow block mb-2 flex items-center justify-between">
                        <span>Devanagari output</span>
                        <button
                            type="button"
                            onClick={copy}
                            disabled={!output}
                            className="inline-flex items-center gap-1 text-[10px] normal-case tracking-normal font-semibold text-[var(--lt-terracotta)] hover:text-[var(--lt-text)] disabled:opacity-40"
                            data-testid="transliterate-copy-btn"
                        >
                            {copied ? (
                                <>
                                    <Check size={12} /> Copied
                                </>
                            ) : (
                                <>
                                    <Copy size={12} /> Copy Text
                                </>
                            )}
                        </button>
                    </label>
                    <textarea
                        readOnly
                        rows={6}
                        value={output}
                        className="w-full bg-[var(--lt-bg)] border border-[var(--lt-border)] rounded-xl px-4 py-3 focus:outline-none resize-none font-nepali text-lg leading-relaxed text-[var(--lt-text)]"
                        placeholder="देवनागरी यहाँ देखिन्छ…"
                        data-testid="transliterate-output"
                    />
                </div>
            </div>

            <p className="mt-4 text-[11px] text-[var(--lt-muted)]">
                Tip: double the vowel for a long sound —{" "}
                <code className="font-mono-stat text-[var(--lt-text)]">nepaal</code> →
                <span className="font-nepali ml-1">नेपाल</span>. Capital{" "}
                <code className="font-mono-stat text-[var(--lt-text)]">M</code>{" "}
                gives anusvara (ं).
            </p>
        </div>
    );
}

/* ---------- Word & Character Counter ---------- */
function WordCounterCard() {
    const [text, setText] = useState("");

    const stats = (() => {
        const trimmed = text.trim();
        const words = trimmed ? trimmed.split(/\s+/).filter(Boolean).length : 0;
        const characters = text.length;
        const sentences = trimmed
            ? (trimmed.match(/[.!?।॥…]+(?=\s|$)/g) || [trimmed]).length
            : 0;
        return { words, characters, sentences };
    })();

    return (
        <div className="glass rounded-2xl p-5 sm:p-8" data-testid="util-word-counter">
            <Header
                icon={<FileText className="text-[var(--lt-cyan)]" />}
                label="Word Counter"
                title="Text Analyzer"
                description="Analyze your writing instantly with real-time word, character, and sentence counting statistics."
            />

            <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={6}
                placeholder="Paste or type your text here to analyze..."
                className="mt-6 w-full bg-[var(--lt-bg-2)] border border-[var(--lt-border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--lt-terracotta)] resize-none text-base leading-relaxed"
                data-testid="word-counter-input"
            />

            <div className="mt-4 grid grid-cols-3 gap-3" data-testid="word-counter-stats">
                <StatBadge
                    label="Words"
                    value={stats.words}
                    accent="text-[var(--lt-terracotta)]"
                    testid="word-counter-words"
                />
                <StatBadge
                    label="Characters"
                    value={stats.characters}
                    accent="text-[var(--lt-amber)]"
                    testid="word-counter-characters"
                />
                <StatBadge
                    label="Sentences"
                    value={stats.sentences}
                    accent="text-[var(--lt-emerald)]"
                    testid="word-counter-sentences"
                />
            </div>

            <div className="mt-4 flex justify-end">
                <button
                    type="button"
                    onClick={() => setText("")}
                    disabled={!text}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--lt-muted)] hover:text-[var(--lt-terracotta)] transition-colors disabled:opacity-40"
                    data-testid="word-counter-clear-btn"
                >
                    <Eraser size={13} /> Clear Text
                </button>
            </div>
        </div>
    );
}

function StatBadge({ label, value, accent, testid }) {
    return (
        <div
            className="rounded-xl border border-[var(--lt-border)] bg-[var(--lt-bg-2)]/60 px-3 py-3 text-center"
            data-testid={testid}
        >
            <div className={`font-mono-stat text-2xl sm:text-3xl ${accent}`}>
                {value}
            </div>
            <div className="eyebrow text-[10px] mt-1">{label}</div>
        </div>
    );
}

/* ---------- Holidays & Festivals ---------- */
function HolidaysCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const load = async () => {
        setLoading(true);
        setErr("");
        try {
            const r = await api.get("/utils/holidays?limit=8");
            setData(r.data);
        } catch (e) {
            setErr("Unable to load holidays right now.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const daysUntil = (dateStr) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const target = new Date(dateStr + "T00:00:00");
        return Math.round((target - today) / 86400000);
    };

    const fmt = (dateStr) => {
        const d = new Date(dateStr + "T00:00:00");
        return d.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
        });
    };

    return (
        <div className="glass rounded-2xl p-5 sm:p-8" data-testid="util-holidays">
            <div className="flex items-start justify-between">
                <Header
                    icon={<PartyPopper className="text-[var(--lt-terracotta)]" />}
                    label="Festivals & Holidays"
                    title="Upcoming in Nepal"
                    description="Stay updated on upcoming national festivals, official holidays, and cultural events in Nepal."
                />
                <button
                    onClick={load}
                    className="btn-ghost-line text-xs inline-flex items-center gap-1"
                    data-testid="holidays-refresh-btn"
                >
                    <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {loading && <SkeletonRows count={5} />}
            {err && !loading && <p className="mt-6 text-sm text-red-400">{err}</p>}

            {data && !loading && (
                <>
                    <ul className="mt-6 divide-y divide-[var(--lt-border)]">
                        {data.upcoming.map((h) => {
                            const dd = daysUntil(h.date);
                            return (
                                <li
                                    key={`${h.date}-${h.en}`}
                                    className="py-3 flex items-center gap-2 sm:gap-3"
                                    data-testid={`holiday-${h.date}`}
                                >
                                    <div
                                        className={`shrink-0 rounded-lg w-12 sm:w-14 text-center py-2 ${
                                            h.type === "national"
                                                ? "bg-[var(--lt-terracotta)]/15 text-[var(--lt-terracotta)]"
                                                : "bg-[var(--lt-amber)]/15 text-[var(--lt-amber)]"
                                        }`}
                                    >
                                        <div className="font-mono-stat text-[10px] sm:text-xs opacity-80 whitespace-nowrap">
                                            {fmt(h.date).split(",")[1]?.trim()}
                                        </div>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="font-semibold text-sm sm:text-base truncate">
                                            {h.en}
                                        </div>
                                        <div className="font-nepali text-xs sm:text-sm text-[var(--lt-muted)] truncate">
                                            {h.np}
                                        </div>
                                    </div>
                                    <div className="shrink-0 text-right">
                                        <div className="font-mono-stat text-xs sm:text-sm whitespace-nowrap">
                                            {dd === 0
                                                ? "Today"
                                                : dd === 1
                                                ? "Tomorrow"
                                                : `in ${dd}d`}
                                        </div>
                                        <div className="eyebrow text-[9px] mt-0.5 hidden sm:block">
                                            {h.type}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                    <FootMeta date={data.today} source={data.source} />
                </>
            )}
        </div>
    );
}

/* ---------- Fuel Prices ---------- */
function FuelCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const load = async () => {
        setLoading(true);
        setErr("");
        try {
            const r = await api.get("/utils/fuel");
            setData(r.data);
        } catch (e) {
            setErr("Unable to load fuel prices right now.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const iconFor = (key) => {
        if (key === "petrol") return <Fuel className="text-[var(--lt-terracotta)]" size={18} />;
        if (key === "diesel") return <Fuel className="text-[var(--lt-amber)]" size={18} />;
        if (key === "kerosene") return <Sparkles className="text-[var(--lt-cyan)]" size={18} />;
        if (key === "lpg") return <Flame className="text-[var(--lt-emerald)]" size={18} />;
        return <Fuel size={18} />;
    };

    return (
        <div className="glass rounded-2xl p-5 sm:p-8" data-testid="util-fuel">
            <div className="flex items-start justify-between">
                <Header
                    icon={<Fuel className="text-[var(--lt-amber)]" />}
                    label="Fuel Prices"
                    title="NOC Official Rates"
                    description="Monitor the latest official Nepal Oil Corporation (NOC) retail rates for petrol, diesel, and kerosene."
                />
                <button
                    onClick={load}
                    className="btn-ghost-line text-xs inline-flex items-center gap-1"
                    data-testid="fuel-refresh-btn"
                >
                    <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {loading && <SkeletonRows count={4} />}
            {err && !loading && <p className="mt-6 text-sm text-red-400">{err}</p>}

            {data && !loading && (
                <>
                    <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {data.items.map((it) => (
                            <li
                                key={it.key}
                                className="rounded-xl border border-[var(--lt-border)] bg-[var(--lt-bg-2)]/60 p-4 flex items-center gap-3"
                                data-testid={`fuel-${it.key}`}
                            >
                                <div className="w-10 h-10 rounded-lg bg-[var(--lt-bg)] flex items-center justify-center shrink-0">
                                    {iconFor(it.key)}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="font-semibold text-sm sm:text-base truncate">
                                        {it.en}
                                    </div>
                                    <div className="font-nepali text-xs text-[var(--lt-muted)] truncate">
                                        {it.np} · {it.unit}
                                    </div>
                                </div>
                                <div className="font-mono-stat text-lg sm:text-xl shrink-0">
                                    Rs {formatNPR(it.price)}
                                </div>
                            </li>
                        ))}
                    </ul>
                    {data.note && (
                        <p className="mt-4 text-[11px] text-[var(--lt-muted)]">{data.note}</p>
                    )}
                    <FootMeta date={data.updated} source={data.source} />
                </>
            )}
        </div>
    );
}

/* ---------- Weather ---------- */
const WEATHER_CODES = {
    0: { label: "Clear", Icon: Sun, color: "text-[var(--lt-amber)]" },
    1: { label: "Mainly clear", Icon: Sun, color: "text-[var(--lt-amber)]" },
    2: { label: "Partly cloudy", Icon: CloudSun, color: "text-[var(--lt-cyan)]" },
    3: { label: "Overcast", Icon: Cloud, color: "text-[var(--lt-muted)]" },
    45: { label: "Fog", Icon: CloudFog, color: "text-[var(--lt-muted)]" },
    48: { label: "Rime fog", Icon: CloudFog, color: "text-[var(--lt-muted)]" },
    51: { label: "Light drizzle", Icon: CloudRain, color: "text-[var(--lt-cyan)]" },
    53: { label: "Drizzle", Icon: CloudRain, color: "text-[var(--lt-cyan)]" },
    55: { label: "Heavy drizzle", Icon: CloudRain, color: "text-[var(--lt-cyan)]" },
    61: { label: "Light rain", Icon: CloudRain, color: "text-[var(--lt-cyan)]" },
    63: { label: "Rain", Icon: CloudRain, color: "text-[var(--lt-cyan)]" },
    65: { label: "Heavy rain", Icon: CloudRain, color: "text-[var(--lt-cyan)]" },
    71: { label: "Light snow", Icon: CloudSnow, color: "text-white" },
    73: { label: "Snow", Icon: CloudSnow, color: "text-white" },
    75: { label: "Heavy snow", Icon: CloudSnow, color: "text-white" },
    80: { label: "Rain showers", Icon: CloudRain, color: "text-[var(--lt-cyan)]" },
    81: { label: "Heavy showers", Icon: CloudRain, color: "text-[var(--lt-cyan)]" },
    82: { label: "Violent showers", Icon: CloudRain, color: "text-[var(--lt-cyan)]" },
    95: { label: "Thunderstorm", Icon: CloudLightning, color: "text-[var(--lt-amber)]" },
    96: { label: "Thunderstorm w/ hail", Icon: CloudLightning, color: "text-[var(--lt-amber)]" },
    99: { label: "Severe thunderstorm", Icon: CloudLightning, color: "text-[var(--lt-amber)]" },
};

function WeatherCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const load = async () => {
        setLoading(true);
        setErr("");
        try {
            const r = await api.get("/utils/weather");
            if (r.data.unavailable) {
                setData(null);
                setErr("Weather temporarily unavailable");
            } else {
                setData(r.data);
            }
        } catch (e) {
            setErr("Unable to load weather right now.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    return (
        <div
            className="glass rounded-2xl p-5 sm:p-8"
            data-testid="util-weather"
        >
            <div className="flex items-start justify-between">
                <Header
                    icon={<CloudSun className="text-[var(--lt-cyan)]" />}
                    label="Weather"
                    title="Major Nepal Cities"
                    description="Live atmospheric updates, temperature tracking, and regional conditions across major Nepalese cities."
                />
                <button
                    onClick={load}
                    className="btn-ghost-line text-xs inline-flex items-center gap-1"
                    data-testid="weather-refresh-btn"
                >
                    <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {loading && <SkeletonRows count={4} />}
            {err && !loading && <p className="mt-6 text-sm text-red-400">{err}</p>}

            {data && !loading && (
                <>
                    <div className="mt-6 grid gap-3 grid-cols-2">
                        {data.cities.map((c) => {
                            const w = WEATHER_CODES[c.weather_code] || {
                                label: "—",
                                Icon: Cloud,
                                color: "text-[var(--lt-muted)]",
                            };
                            const Icon = w.Icon;
                            return (
                                <div
                                    key={c.id}
                                    className="rounded-xl border border-[var(--lt-border)] bg-[var(--lt-bg-2)]/60 p-4"
                                    data-testid={`weather-${c.id}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="min-w-0">
                                            <div className="font-semibold text-sm truncate">
                                                {c.en}
                                            </div>
                                            <div className="font-nepali text-xs text-[var(--lt-muted)] truncate">
                                                {c.np}
                                            </div>
                                        </div>
                                        <Icon className={w.color} size={28} />
                                    </div>
                                    <div className="mt-3 font-mono-stat text-3xl">
                                        {Math.round(c.temperature)}°
                                        <span className="text-sm text-[var(--lt-muted)] ml-1">
                                            C
                                        </span>
                                    </div>
                                    <div className="text-xs text-[var(--lt-muted)]">
                                        {w.label} · feels {Math.round(c.feels_like)}°
                                    </div>
                                    <div className="mt-3 flex items-center gap-3 text-[11px] text-[var(--lt-muted)] font-mono-stat">
                                        <span className="inline-flex items-center gap-1">
                                            <Droplets size={11} /> {c.humidity}%
                                        </span>
                                        <span className="inline-flex items-center gap-1">
                                            <Wind size={11} />{" "}
                                            {Math.round(c.wind_kmh)} km/h
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <FootMeta
                        date={data.cities[0]?.observed_at}
                        source={data.source}
                    />
                </>
            )}
        </div>
    );
}

/* ---------- Shared ---------- */
function Header({ icon, label, title, description }) {
    return (
        <div>
            <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-[var(--lt-bg-2)] flex items-center justify-center">
                    {icon}
                </div>
                <div>
                    <p className="eyebrow">{label}</p>
                    <h2 className="text-xl font-semibold mt-0.5">{title}</h2>
                </div>
            </div>
            {description && (
                <p
                    className="mt-3 mb-1"
                    style={{ color: "#8892B0", fontSize: "13px", lineHeight: 1.5 }}
                >
                    {description}
                </p>
            )}
        </div>
    );
}

function SkeletonRows({ count }) {
    return (
        <div className="mt-6 space-y-3">
            {Array.from({ length: count }).map((_, i) => (
                <div
                    key={i}
                    className="h-14 rounded-lg bg-[var(--lt-bg-2)]/60 animate-pulse"
                />
            ))}
        </div>
    );
}

function FootMeta({ date, source }) {
    let displayDate = date;
    if (date) {
        try {
            const d = new Date(date);
            if (!Number.isNaN(d.getTime())) displayDate = d.toDateString();
        } catch (_) {
            // keep original
        }
    }
    return (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-2 text-[11px] text-[var(--lt-muted)]">
            <span>Updated: {displayDate || "—"}</span>
            <span>Source: {source}</span>
        </div>
    );
}

function formatNPR(n) {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
    return Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}
